import clsx from 'clsx';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  useNotify,
} from 'core/hooks';
import {
  ClaimDTO,
} from 'dtos';
import Select from 'core/components/Select';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';
import {
  useMarkClaimStatus,
} from 'pages/Dashboard/pages/Billing/hooks';

type Part = 'select' | 'root' | 'label';

type Props = {
  claim: Partial<ClaimDTO>;
  onSave?: () => Promise<boolean | undefined>;
  classes?: Partial<Record<Part, string>>;
};

export default function ClaimStatusSelect({
  claim,
  onSave,
  classes,
}: Props) {
  const {
    mutateAsync: updateClaimStatus,
    isPending: updatingStatus,
  } = useMarkClaimStatus();
  const notify = useNotify();
  const { claimId, encounterId, status, validManualStatusTransitions } = claim ?? {};
  const colors = useColorsHashMap();

  const [claimStatus, setClaimStatus] = useState<string>(status ?? '');
  const statusColor = colors?.[claimStatus ?? ''] ?? '!bg-brown-100 !text-brown-700';

  useEffect(() => {
    setClaimStatus(status ?? '');
  }, [status]);

  return (
    <Select<string>
      options={validManualStatusTransitions ?? []}
      value={claimStatus}
      muiClasses={{
        select: clsx(
          'rounded',
          {
            '!bg-gray-100 !cursor-wait': updatingStatus,
            [statusColor]: !updatingStatus,
          },
          classes?.select,
        ),
      }}
      placeholder={status ?? 'Select'}
      selectClassName={clsx('!border-0 !pointer-events-auto', classes?.root)}
      renderValue={(value) => (updatingStatus ? 'Updating...' : value)}
      disabled={updatingStatus || (validManualStatusTransitions ?? []).length <= 1}
      getValue={(option) => option}
      getLabel={(option) => option}
      onChange={async (value) => {
        try {
          setClaimStatus(value as string);
          const isSavedSuccess = await onSave?.() ?? true;

          if (isSavedSuccess) {
            await updateClaimStatus({
              claimStatus: value as string,
              claimId,
            });
            billingEventChannel.emit('onChargesInfoChange', {
              encounterId,
              id: claimId ?? 0,
            });
            billingEventChannel.emit('onClaimLogChange', claimId ?? 0);
            notify({
              message: 'Status updated successfully!',
              variant: 'success',
            });
          } else {
            setClaimStatus(status ?? '');
          }
        } catch (error) {
          setClaimStatus(status ?? '');
        }
      }}
    />
  );
}
